import moment, { Moment, Duration } from 'moment-timezone';
import { CoreTimeSegment } from '@densityco/lib-api-types';
import { DayOfWeek } from '@densityco/lib-common-types';

// TimeFilter type for use in applications
export type TimeFilter = {
  start: Duration;
  end: Duration;
  days: Array<DayOfWeek>;
};

// Validate a string that containes a serialized representation of a TimeFilter
export const TIME_FILTER_REGEX = /^(sun|mon|tue|wed|thu|fri|sat)(\+(sun|mon|tue|wed|thu|fri|sat))*:\d{4}-\d{4}$/;
export const TIME_FILTER_DAYS: { [key: string]: DayOfWeek } = {
  sun: DayOfWeek.SUNDAY,
  mon: DayOfWeek.MONDAY,
  tue: DayOfWeek.TUESDAY,
  wed: DayOfWeek.WEDNESDAY,
  thu: DayOfWeek.THURSDAY,
  fri: DayOfWeek.FRIDAY,
  sat: DayOfWeek.SATURDAY,
};

// Convert a serialized representation into a TimeFilter object
// E.g. "Mon+Wed+Fri:0800-1700"
export function stringToTimeFilter(value: string): TimeFilter {
  value = value.toLowerCase();
  if (!TIME_FILTER_REGEX.test(value)) {
    throw new Error('Invalid time filter.');
  }
  const [daysComponent, timesComponent] = value.split(':');
  const [timeStart, timeEnd] = timesComponent.split('-');
  return {
    start: moment.duration(`${timeStart.slice(0, 2)}:${timeStart.slice(2, 4)}`),
    end: moment.duration(`${timeEnd.slice(0, 2)}:${timeEnd.slice(2, 4)}`),
    days: daysComponent.split('+').map((day) => TIME_FILTER_DAYS[day]),
  };
}

// Convert a TimeFilter object back into a serialized string
// This string can then be sent to the API, e.g. &time_filters=Mon+Wed+Fri:0800-1700
export function timeFilterToString(value: TimeFilter) {
  const segmentStartString = moment.utc().startOf('day').add(value.start).format('HHmm');
  const segmentEndString = moment.utc().startOf('day').add(value.end).format('HHmm');
  return `${value.days.map((x) => x.slice(0, 3).toLowerCase()).join('+')}:${segmentStartString}-${segmentEndString}`;
}

// Convert a legacy "time segment" object returned by API v2 into an equivalent TimeFilter
export function timeSegmentToTimeFilter(segment: CoreTimeSegment): TimeFilter {
  return {
    start: moment.duration(segment.start),
    end: moment.duration(segment.end),
    days: segment.days,
  };
}

// Check if a timestamp is in a time filter
export function isTimestampInTimeFilter(timestamp: Moment, filter: TimeFilter) {
  const timestampMillis = timestamp.valueOf() - timestamp.clone().startOf('day').valueOf();
  const filterStartMillis = filter.start.asMilliseconds();
  const filterEndMillis = filter.end.asMilliseconds();
  return (
    (timestampMillis >= filterStartMillis &&
      timestampMillis < filterEndMillis &&
      filter.days.includes(timestamp.format('dddd') as DayOfWeek)) ||
    (timestampMillis >= filterStartMillis &&
      filterEndMillis <= filterStartMillis &&
      filter.days.includes(timestamp.format('dddd') as DayOfWeek)) ||
    (timestampMillis < filterEndMillis &&
      filterEndMillis <= filterStartMillis &&
      filter.days.includes(timestamp.clone().subtract(1, 'day').format('dddd') as DayOfWeek))
  );
}

// Given a Moment and a TimeFilter, this returns the end of the filter
// If the Moment is within the bounds of the filter, otherwise it just returns the moment
export function getLastTimestampInTimeFilter(timestamp: Moment, filter: TimeFilter) {
  if (!isTimestampInTimeFilter(timestamp, filter)) {
    return timestamp;
  }
  const result = timestamp.clone();
  const filterStartMillis = filter.start.asMilliseconds();
  const filterEndMillis = filter.end.asMilliseconds();

  if (filterEndMillis <= filterStartMillis) {
    result.add(1, 'day');
  }
  return result.startOf('day').add(filterEndMillis, 'milliseconds');
}

// Given a Moment and a TimeFilter, this returns the next Moment >= the provided Moment,
// Which would be included in the provided TimeFilter.
export function getNextTimestampInTimeFilter(timestamp: Moment, filter: TimeFilter) {
  const result = timestamp.clone();
  const timestampMillis = result.valueOf() - result.clone().startOf('day').valueOf();
  const filterStartMillis = filter.start.asMilliseconds();
  const filterEndMillis = filter.end.asMilliseconds();

  if (timestampMillis < filterEndMillis && filter.days.includes(result.format('dddd') as DayOfWeek)) {
    return result.startOf('day').add(Math.max(timestampMillis, filterStartMillis), 'milliseconds');
  }

  for (let i = 0; i < 7; i++) {
    result.add(1, 'day');
    if (filter.days.includes(result.format('dddd') as DayOfWeek)) {
      return result.startOf('day').add(filterStartMillis, 'milliseconds');
    }
  }

  throw new Error('Invalid time filter.');
}
